import React, { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { normalizeGameMode, GAME_MODE_CATALOG, type GameMode } from '@interactive-room';
import { PollBuilder } from '../studio/builders/PollBuilder';
import { ArenaBuilder } from '../studio/builders/ArenaBuilder';
import { CoopBuilder } from '../studio/builders/CoopBuilder';
import { SandboxBuilder } from '../studio/builders/SandboxBuilder';
import { SequencerBuilder } from '../studio/builders/SequencerBuilder';
import { PinpointBuilder } from '../studio/builders/PinpointBuilder';
import { WallBuilder } from '../studio/builders/WallBuilder';
import { VinculaBuilder } from '../studio/builders/VinculaBuilder';
import type { ActivityDraft } from '../studio/activityStorage';
import { StudioEditorProvider } from '../studio/StudioEditorContext';
import { getSubjectById, getUseCaseById } from '../studio/constants/subjectCatalog';
import {
  isStandaloneActivityMode,
  STANDALONE_ACTIVITY_MODES,
} from '../studio/constants/standaloneActivityModes';
import type { StudioLaunchOptions } from '../studio/studioLaunch';

type StandaloneBuilderMode = (typeof STANDALONE_ACTIVITY_MODES)[number];

const BUILDERS: Record<
  StandaloneBuilderMode,
  React.ComponentType<
    StudioLaunchOptions & { draftId?: string; embedded?: boolean; onSaved?: (d: ActivityDraft) => void }
  >
> = {
  poll: PollBuilder,
  arena: ArenaBuilder,
  coop: CoopBuilder,
  sandbox: SandboxBuilder,
  order: SequencerBuilder,
  point: PinpointBuilder,
  wall: WallBuilder,
  vincula: VinculaBuilder,
};

function StudioModeUnsupportedState({ modeLabel }: { modeLabel?: string }) {
  return (
    <div className="studio-page mx-auto w-full max-w-lg py-16 text-center">
      <p className="font-display text-2xl text-[var(--sala-ink)]">
        Modo no disponible como actividad suelta
      </p>
      <p className="mt-4 text-sm leading-relaxed text-[var(--sala-ink-muted)]">
        {modeLabel ? (
          <>
            <strong className="text-[var(--sala-ink)]">{modeLabel}</strong> está diseñado para vivir
            dentro de una Guía o Clase Interactiva.
          </>
        ) : (
          <>Este modo está diseñado para vivir dentro de una Guía o Clase Interactiva.</>
        )}{' '}
        Créalo desde el Constructor de Material en tu asignatura.
      </p>
      <Link to="/studio" className="sala-btn-hero mt-8 inline-flex text-xs">
        Volver al Studio
      </Link>
    </div>
  );
}

export function StudioModePage() {
  const { mode: rawMode } = useParams<{ mode: string }>();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draft') ?? undefined;
  const launchSubjectId = searchParams.get('subject') ?? undefined;
  const launchPresetId = searchParams.get('preset') ?? undefined;
  const useCaseId = searchParams.get('useCase') ?? undefined;
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  const gameMode = normalizeGameMode(rawMode);
  const isSupported = gameMode != null && isStandaloneActivityMode(gameMode);
  const Builder = isSupported ? BUILDERS[gameMode as StandaloneBuilderMode] : null;
  const subject = launchSubjectId ? getSubjectById(launchSubjectId) : undefined;
  const useCase = useCaseId ? getUseCaseById(useCaseId) : undefined;

  if (!gameMode || !isSupported || !Builder) {
    const modeLabel =
      gameMode && GAME_MODE_CATALOG[gameMode as GameMode]
        ? GAME_MODE_CATALOG[gameMode as GameMode].displayName
        : rawMode;
    return <StudioModeUnsupportedState modeLabel={modeLabel} />;
  }

  const meta = GAME_MODE_CATALOG[gameMode];
  const backTo =
    subject && subject.id !== 'general' ? `/studio/asignatura/${subject.id}` : '/studio/herramientas';

  return (
    <div className="studio-page w-full min-w-0">
      <StudioEditorProvider subjectId={launchSubjectId} presetId={launchPresetId} useCaseId={useCaseId}>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Link to="/studio" className="sala-label text-[var(--sala-accent-neon)]">
            Studio
          </Link>
          {subject && subject.id !== 'general' && (
            <>
              <span className="text-[var(--sala-ink-dim)]">/</span>
              <Link to={backTo} className="sala-label text-[var(--sala-accent-neon)]">
                {subject.icon} {subject.name}
              </Link>
            </>
          )}
          <span className="text-[var(--sala-ink-dim)]">/</span>
          <span className="sala-label">
            {useCase ? useCase.title : `${meta.icon} ${meta.displayName}`}
          </span>
        </div>

        {savedMsg && (
          <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
            {savedMsg}
          </div>
        )}

        <Builder
          draftId={draftId}
          launchSubjectId={launchSubjectId}
          launchPresetId={launchPresetId}
          onSaved={(draft) => setSavedMsg(`Borrador guardado: «${draft.title}»`)}
        />
      </StudioEditorProvider>
    </div>
  );
}
